import { useState, useEffect, useCallback } from 'react';
import Login from './components/auth/Login';
import UserMenu from './components/auth/UserMenu';
import AdminPanel from './components/admin/AdminPanel';
import { UnifiedDashboard } from './components/unified/UnifiedDashboard';
import { ClientViewDashboard } from './components/client-view/ClientViewDashboard';
import { BrokerViewDashboard } from './components/broker-view/BrokerViewDashboard';
import { SalesViewDashboard } from './components/sales-view/SalesViewDashboard';
import { siftApiClient, type UserSession } from './api/siftApiClient';
import { canUseFeature, type FeatureFlag } from './featureFlags';
import { useThemeInitialization } from './hooks/useTheme';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { loadSessionWithRetry } from './auth/sessionRecovery';
import './App.css';
import './styles/glass-theme.css';
import { setUsageView, startClickCapture, track } from './telemetry/usage';

type ViewId = 'client-view' | 'broker-view' | 'sales-view' | 'main';

/**
 * The selectable top-level views, each gated by a feature flag. Order defines
 * both the toggle order and the fallback priority (first accessible view wins
 * when a user can't access their current/default view). Add future views here —
 * the toggle, default landing view, access-pinning, and routing all derive from
 * this list, so no per-view branching is needed elsewhere.
 */
interface ViewDef {
  id: ViewId;
  label: string;
  feature: FeatureFlag;
}

const VIEWS: ViewDef[] = [
  { id: 'client-view', label: 'Client View', feature: 'client_view' },
  { id: 'broker-view', label: 'Broker View', feature: 'broker_view' },
  { id: 'sales-view', label: 'Sales View', feature: 'sales_view' },
  { id: 'main', label: 'Main', feature: 'main' },
];

/**
 * Admin OR flag, mirroring the backend route guards (`_require_sales_view`,
 * `_require_broker_admin`). `hasFeature` alone would hide a NON-DEFAULT view
 * from an admin whose flags are set but do not list it — the backend would
 * serve every route while the tab stayed invisible.
 */
const canAccessView = (user: UserSession, view: ViewDef): boolean =>
  canUseFeature(user.is_admin, user.feature_flags, view.feature);

function AppContent() {
  // Initialize theme system
  useThemeInitialization();
  const auth = useAuth();
  const [user, setUser] = useState<UserSession | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [backendUnreachable, setBackendUnreachable] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [impersonatedRole, setImpersonatedRole] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ViewId>(VIEWS[0].id);

  useEffect(() => {
    if (!user) return;
    const usageView = activeView === 'main' ? 'main' : (activeView.replace('-', '_') as 'client_view' | 'broker_view' | 'sales_view');
    setUsageView(usageView);
    startClickCapture();
    track(usageView, 'view', 'open');
  }, [activeView, user]);

  // Check if user can access admin panel (admin, manager with 10/13 char role,
  // or a broker_admin — the latter is scoped to the Broker Settings section).
  const canAccessAdminPanel = (userSession: UserSession): boolean => {
    if (userSession.is_admin) return true;
    if ((userSession.feature_flags ?? []).includes('broker_admin')) return true;
    const roles = userSession.role.split(',').map(r => r.trim());
    // Manager roles (10 or 13 character territory codes).
    return roles.some(r => r.length === 10 || r.length === 13);
  };

  useEffect(() => {
    if (auth.loading) return;

    let cancelled = false;
    const checkAuth = async () => {
      // Always ask, whatever the sign-in method. `entraEnabled` means Entra is
      // AVAILABLE, not that this user used it — gating on the MSAL token alone
      // sent password-authenticated users to the login screen on every reload,
      // because their legacy bearer and session cookie were never consulted.
      // The request interceptor supplies whichever credential exists, and a 401
      // is now classified as genuinely signed out.
      const result = await loadSessionWithRetry(() =>
        siftApiClient.me(auth.user?.accessToken ?? null));
      if (cancelled) return;
      setUser(result.status === 'ok' ? result.session : null);
      setBackendUnreachable(result.status === 'unreachable');
      setAuthChecked(true);
    };
    checkAuth();
    return () => { cancelled = true; };
  }, [auth.loading, auth.user?.accessToken, retryToken]);

  // Keep the active view pinned to a feature the user can actually access. If
  // their current view isn't accessible (e.g. fresh login landing on the default
  // view they lack), fall back to the first view they do have.
  //
  // A deep link (?view=sales-view) is a REQUEST, not an override: it is checked
  // against the same access rules as the toggle, so a forwarded plan email
  // cannot open a view the recipient is not entitled to.
  useEffect(() => {
    if (!user) return;
    const accessible = VIEWS.filter((v) => canAccessView(user, v));
    const requested = new URLSearchParams(window.location.search).get('view');
    setActiveView((current) => {
      const target = accessible.find((v) => v.id === requested);
      if (target) return target.id;
      if (accessible.some((v) => v.id === current)) return current;
      return accessible[0]?.id ?? current;
    });
  }, [user]);

  const handleLoginSuccess = useCallback(async () => {
    try {
      const session = await siftApiClient.me(auth.user?.accessToken ?? null);
      setUser(session);
    } catch {
      setUser(null);
    }
  }, [auth.user?.accessToken]);

  const handleLogout = useCallback(() => {
    setUser(null);
    setShowAdminPanel(false);
  }, []);

  // Show loading screen while checking auth
  if (!authChecked) {
    return (
      <div className="app glass-app-background loading-screen">
        <div className="loading-content glass-card">
          <h1>SIFT</h1>
          <p className="glass-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // Entra-authenticated but not provisioned in USERS table yet
  if (user && user.provisioned === false) {
    return (
      <div className="app glass-app-background loading-screen">
        <div className="loading-content glass-card" style={{ textAlign: 'center', maxWidth: '480px' }}>
          <h1>SIFT</h1>
          <p className="glass-text-primary" style={{ fontSize: '1.1rem', marginTop: '1rem' }}>
            Account not provisioned
          </p>
          <p className="glass-text-secondary" style={{ marginTop: '0.5rem' }}>
            Your sign-in was successful but your account ({user.email}) has not been provisioned for SIFT yet.
            <br /><br />
            Please reach out to your administrator to request access.
          </p>
          <button
            className="glass-button"
            style={{ marginTop: '1.5rem' }}
            onClick={() => {
              siftApiClient.clearAccessToken();
              setUser(null);
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Backend unreachable — checked BEFORE the login gate below, because the whole
  // point is that this is not an authentication failure and a sign-in cannot fix it.
  if (backendUnreachable) {
    return (
      <div className="app glass-app-background loading-screen">
        <div className="loading-content glass-card" style={{ textAlign: 'center', maxWidth: '480px' }}>
          <h1>SIFT</h1>
          <p className="glass-text-primary" style={{ fontSize: '1.1rem', marginTop: '1rem' }}>
            Can't reach SIFT right now
          </p>
          <p className="glass-text-secondary" style={{ marginTop: '0.5rem' }}>
            You are still signed in — the service did not respond. This usually clears
            on its own in a few moments.
          </p>
          <button
            className="glass-button"
            style={{ marginTop: '1.5rem' }}
            onClick={() => {
              setBackendUnreachable(false);
              setAuthChecked(false);
              setRetryToken((n) => n + 1);
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Main authenticated app
  // Use impersonated role for dashboard if set (admin feature), otherwise use actual role
  const effectiveRole = impersonatedRole || user.role;

  // Feature gating: admin OR the flag, the same rule the backend's `_require_agent` applies.
  // `hasFeature` alone hid the widget from an admin the API would have served, and
  // sales_mia_agent is NON-DEFAULT, so that was every admin not granted it by hand.
  const canMiaAgent = canUseFeature(user.is_admin, user.feature_flags, 'rds_mia_agent');
  // Both are Mia to the user; the flags are separate because the tool surfaces are.
  const canSalesMiaAgent = canUseFeature(user.is_admin, user.feature_flags, 'sales_mia_agent');
  const accessibleViews = VIEWS.filter((v) => canAccessView(user, v));
  const showViewToggle = accessibleViews.length > 1;
  // The view to actually render: the active one if the user can access it,
  // else the first view they can (guards the first render before the pinning
  // effect settles, e.g. a fresh login on a default view they lack).
  const viewToRender = accessibleViews.some((v) => v.id === activeView)
    ? activeView
    : accessibleViews[0]?.id;

  const renderView = (id: ViewId | undefined) => {
    switch (id) {
      case 'main':
        return <UnifiedDashboard userRole={effectiveRole} impersonatedRole={impersonatedRole} miaEnabled={canMiaAgent} />;
      case 'broker-view':
        return <BrokerViewDashboard userRole={effectiveRole} impersonatedRole={impersonatedRole} />;
      case 'client-view':
        return <ClientViewDashboard userRole={effectiveRole} impersonatedRole={impersonatedRole} agentEnabled={canMiaAgent} />;
      case 'sales-view':
        return <SalesViewDashboard userRole={effectiveRole} impersonatedRole={impersonatedRole} agentEnabled={canSalesMiaAgent} />;
      default:
        return null;
    }
  };

  return (
    <div className="app glass-app-background">
      <header className="app-header glass-card-subtle">
        <div className="app-header-left">
          <div className="app-branding">
            <h1 className="app-title">SIFT</h1>
            <span className="app-subtitle glass-text-secondary">Strategic Insights Field Tool</span>
          </div>
          {impersonatedRole && (
            <span className="impersonation-badge">
              Viewing as: {impersonatedRole}
              <button 
                className="clear-impersonation" 
                onClick={() => setImpersonatedRole(null)}
                title="Stop impersonating"
              >
                ✕
              </button>
            </span>
          )}
        </div>
        <div className="app-header-center">
          {showViewToggle && (
            <div className="view-toggle">
              {accessibleViews.map((v) => (
                <button
                  key={v.id}
                  className={`view-toggle-btn ${activeView === v.id ? 'active' : ''}`}
                  onClick={() => setActiveView(v.id)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="app-header-right">
          <UserMenu 
            user={user} 
            onLogout={handleLogout}
            onOpenAdmin={canAccessAdminPanel(user) ? () => setShowAdminPanel(true) : undefined}
          />
        </div>
      </header>
      
      <main className="app-main">
        {accessibleViews.length === 0 ? (
          <div className="no-features glass-card">
            <h2 className="glass-text-primary">No features enabled</h2>
            <p className="glass-text-secondary">
              Your account doesn't have access to any features yet. Contact an administrator.
            </p>
          </div>
        ) : (
          renderView(viewToRender)
        )}
      </main>
      
      {showAdminPanel && (
        <AdminPanel 
          onClose={() => setShowAdminPanel(false)} 
          impersonatedRole={impersonatedRole}
          onImpersonate={setImpersonatedRole}
          user={user}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
